/**
 * Global audits list — every audit across every project, newest first.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { FileSearch, Plus, Search } from 'lucide-react';
import type { Audit, Project } from '@seo/types';
import { Page, PageHeader, PageToolbar } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table';
import { EmptyState } from '@/components/ui/empty';
import { ErrorBanner } from '@/components/error-banner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusPill } from '@/components/status-pill';
import { CreateAuditDialog } from '@/features/audits/create-audit-dialog';
import { useAudits } from '@/features/audits/use-audits';
import { useProjects } from '@/features/projects/use-projects';
import { relTime } from '@/lib/format';

export function AuditsRoute(): JSX.Element {
  const audits = useAudits();
  const navigate = useNavigate();
  const projects = useProjects();
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const projectsById = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects.data?.items ?? []) m.set(p.id, p);
    return m;
  }, [projects.data]);

  const rows = useMemo(() => {
    const items = audits.data?.items ?? [];
    const q = query.trim().toLowerCase();
    const sorted = [...items].sort((a, b) =>
      (b.created_at ?? '').localeCompare(a.created_at ?? ''),
    );
    if (!q) return sorted;
    return sorted.filter((a) => {
      const p = projectsById.get(a.project_id);
      return (
        a.status.toLowerCase().includes(q) ||
        (p?.name.toLowerCase().includes(q) ?? false) ||
        (p?.domain.toLowerCase().includes(q) ?? false)
      );
    });
  }, [audits.data, projectsById, query]);

  return (
    <Page>
      <PageHeader
        title="Site audits"
        description="We crawl your site, spot technical and content problems, and give you a health score plus a to-do list."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            New audit
          </Button>
        }
      />
      <PageToolbar>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by project, domain, or status..."
            className="h-9 pl-8"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {rows.length} of {audits.data?.items.length ?? 0}
        </span>
      </PageToolbar>

      {audits.error ? <ErrorBanner error={audits.error} /> : null}

      <DataTable<Audit>
        loading={audits.isLoading}
        rows={rows}
        rowKey={(a) => a.id}
        onRowClick={(a) =>
          void navigate({ to: '/audits/$auditId', params: { auditId: a.id } })
        }
        columns={[
          {
            key: 'status',
            header: 'Status',
            cell: (a) => <StatusPill status={a.status} />,
            className: 'w-32',
          },
          {
            key: 'project',
            header: 'Project',
            cell: (a) => {
              const p = projectsById.get(a.project_id);
              return (
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {p?.name ?? a.project_id.slice(0, 8)}
                  </div>
                  {p ? (
                    <div className="truncate text-xs text-muted-foreground">
                      {p.domain.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </div>
                  ) : null}
                </div>
              );
            },
          },
          {
            key: 'score',
            header: 'Health',
            cell: (a) =>
              a.health_score !== null ? (
                <Badge variant="outline" className="tabular font-mono text-[10px]">
                  {a.health_score}
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              ),
            align: 'right',
            className: 'w-24',
          },
          {
            key: 'when',
            header: 'Started',
            cell: (a) => (
              <span className="text-xs text-muted-foreground">
                {relTime(a.started_at ?? a.created_at)}
              </span>
            ),
            align: 'right',
            className: 'w-32',
          },
          {
            key: 'open',
            header: '',
            cell: (a) => (
              <Link
                to="/audits/$auditId"
                params={{ auditId: a.id }}
                className="text-xs font-medium text-primary hover:underline"
              >
                Open →
              </Link>
            ),
            align: 'right',
            className: 'w-20',
          },
        ]}
        emptyState={
          <EmptyState
            icon={FileSearch}
            title="Run your first audit"
            description="We'll crawl your site and hand you an actionable to-do list you can send to your dev or SEO team."
            action={
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                New audit
              </Button>
            }
          />
        }
      />
      <CreateAuditDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </Page>
  );
}
